import { useState } from "react";
import { useSpark } from "@/lib/store";
import { respondGoldThread } from "@/lib/api";
import { toast } from "sonner";
import { SparkSun } from "@/components/SparkSun";

/**
 * Gold Thread Panel — the recurring themes Spark surfaces across activities.
 * Visually connects to the Spark Sun: a fine gold line extends from a sun
 * ray on the left into the emerging theme.
 */
export default function GoldThreadPanel() {
  const { userId, profile, setProfile } = useSpark();
  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState("");

  if (!profile) return null;
  const pending = profile.gold_threads_pending || [];
  const confirmed = (profile.gold_threads_confirmed || []).filter(t => t.origin !== "weave");

  const respond = async (id, status) => {
    const updated = await respondGoldThread(userId, id, { status, correction: note });
    setProfile(updated);
    setOpenId(null);
    setNote("");
    if (status === "yes") toast("Held as one of your Gold Threads.");
    else if (status === "somewhat") toast("Noted — added with your caveat.");
    else toast("Set aside. Spark won't push this one again.");
  };

  if (pending.length === 0 && confirmed.length === 0) return null;

  return (
    <section className="relative">
      {/* Small sun mark on the far left — a ray of it visually becomes the gold thread */}
      <div className="pointer-events-none absolute left-0 top-0 hidden lg:block" aria-hidden="true">
        <SparkSun size={64} color="#C3A56B" strokeWidth={1} />
      </div>

      <div className="lg:pl-24">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-px w-8" style={{ background: "linear-gradient(90deg, transparent, #C3A56B)" }} />
          <span className="spark-eyebrow-copper">The Gold Thread</span>
        </div>

        {pending.map(t => (
          <div key={t.id} data-testid={`gold-thread-item-${t.id}`} className="mb-8 last:mb-0">
            <p className="text-2xl sm:text-3xl leading-snug text-[#17171C] tracking-tight">
              <span className="spark-voice text-[#56565D]">A Gold Thread may be emerging: </span>
              <span className="spark-voice" style={{ color: "#A6894F" }}>{t.theme}.</span>
            </p>
            <p className="mt-4 text-[#56565D] text-base leading-relaxed max-w-3xl">{t.evidence}</p>

            {t.linked_to && t.linked_to.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {t.linked_to.map(l => (
                  <span key={l} className="text-[10px] font-medium uppercase tracking-[0.18em] text-[#A6894F]">
                    connects to {l.replace(/_/g, " ")}
                    <span className="mx-2 inline-block h-1 w-1 rounded-full align-middle bg-[#C3A56B]/70 last:hidden" />
                  </span>
                ))}
              </div>
            )}

            <p className="mt-5 text-sm text-[#8A8A91]">Does that resonate?</p>

            {openId !== `somewhat-${t.id}` && openId !== `no-${t.id}` ? (
              <div className="mt-4 flex flex-wrap gap-2">
                <button data-testid="gold-thread-yes-btn" onClick={() => respond(t.id, "yes")} className="spark-btn-copper">Yes, that resonates</button>
                <button data-testid="gold-thread-somewhat-btn" onClick={() => setOpenId(`somewhat-${t.id}`)} className="spark-btn-secondary">Somewhat</button>
                <button data-testid="gold-thread-no-btn" onClick={() => setOpenId(`no-${t.id}`)} className="spark-btn-secondary">No, not quite</button>
              </div>
            ) : (
              <div className="mt-4">
                <textarea
                  data-testid="gold-thread-correction-input"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  rows={3}
                  placeholder={openId.startsWith("somewhat") ? "Which part fits, and which part doesn't?" : "Tell me what I'm missing."}
                  className="w-full rounded-2xl border border-[rgba(0,0,0,0.12)] bg-white px-5 py-4 text-base leading-relaxed focus:outline-none focus:border-[#C3A56B]"
                />
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => respond(t.id, openId.startsWith("somewhat") ? "somewhat" : "no")}
                    className="spark-btn-primary"
                    data-testid="gold-thread-submit-correction-btn"
                  >
                    Save
                  </button>
                  <button onClick={() => { setOpenId(null); setNote(""); }} className="spark-btn-secondary">Cancel</button>
                </div>
              </div>
            )}
          </div>
        ))}

        {confirmed.length > 0 && (
          <div className="mt-10">
            <p className="spark-eyebrow mb-4">My Gold Threads</p>
            <ul className="grid sm:grid-cols-2 gap-3">
              {confirmed.map(t => (
                <li key={t.id} className="flex items-start gap-3 rounded-xl border border-[rgba(195,165,107,0.35)] p-4"
                    style={{ background: "hsl(var(--spark-warmth))" }}>
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#C3A56B]" />
                  <span className="text-[#17171C] spark-voice text-lg leading-snug">{t.theme}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
